import { useState, useRef, type ReactNode } from 'react'
import { FiFileText, FiSend, FiCheck, FiAlertTriangle, FiLoader, FiExternalLink, FiCopy, FiX, FiSlash } from 'react-icons/fi'
import { SideModal } from './SideModal'
import ConfirmDialog from './ConfirmDialog'

// Boleto do limite de crédito (09/10/2026) — etapa 1 gera o boleto Asaas da
// diferença (pedido − limite) de quem está no filtro; etapa 2 envia pelo número
// do Faturamento (modelo Meta boleto_limite_tupperware). Backend em
// routes/limite-boleto.cjs: uma consultora por chamada, para mostrar o
// andamento linha a linha e poder parar no meio.

export interface BoletoLimite {
  id: number; semana: number; codigo: number; nome: string | null; valor: number; estimado: boolean
  vencimento: string; status: 'gerando' | 'gerado' | 'enviando' | 'enviado' | 'verificar'
  paymentId: string | null; invoiceUrl: string | null; linhaDigitavel: string | null; telefone: string | null
  erro: string | null; geradoPor: string | null; geradoEm: string | null; enviadoPor: string | null; enviadoEm: string | null
}
export interface AlvoBoleto { codigo: number; nome: string; valor: number }
type Fetch = (url: string, options?: RequestInit) => Promise<Response>

export const VALOR_MINIMO_BOLETO = 5
const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const dataBR = (d: string) => { const [a, m, dia] = d.slice(0, 10).split('-'); return `${dia}/${m}/${a}` }
const quando = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''
const isoLocal = (d: Date) => d.toLocaleDateString('sv-SE')
const sem = (s: number) => `${String(s).slice(0, 4)}/${String(s).slice(4)}`

// Rótulo do status — também é o valor do filtro da coluna "Boleto" no painel.
export function rotuloBoleto(b?: BoletoLimite | null): string {
  if (!b) return 'Sem boleto'
  if (b.status === 'enviado') return 'Enviado'
  if (b.status === 'verificar') return 'Conferir no Asaas'
  if (b.status === 'gerado' && b.erro) return 'Gerado · falhou envio'
  if (b.status === 'gerado') return 'Gerado'
  return b.status === 'gerando' ? 'Gerando…' : 'Enviando…'
}

export function ChipBoleto({ b, onClick }: { b?: BoletoLimite | null; onClick: () => void }) {
  if (!b) return null
  const cor = b.status === 'enviado' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : b.status === 'verificar' || b.erro ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    : 'bg-primary/10 text-primary'
  const Icone = b.status === 'enviado' ? FiCheck : b.status === 'verificar' || b.erro ? FiAlertTriangle : FiFileText
  return (
    <button onClick={onClick} title={b.erro || `Vence ${dataBR(b.vencimento)} — clique para detalhes`}
      className={`neu-tag inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap hover:opacity-80 ${cor}`}>
      <Icone className="w-3 h-3" />{rotuloBoleto(b)}
    </button>
  )
}

type Resultado = { ok: boolean; msg?: string }

// Etapa 1 e etapa 2 no mesmo painel lateral: a lista de quem entra, o
// vencimento (só ao gerar), a confirmação e o andamento linha a linha.
export function LoteBoletoPanel({ modo, semana, estimado, alvos: alvosVivos, boletos, pulados: puladosVivos, authenticatedFetch, onAtualizar, onRemovido, onClose }: {
  modo: 'gerar' | 'enviar' | 'cancelar'; semana: number; estimado: boolean
  alvos: AlvoBoleto[]; boletos: Map<number, BoletoLimite>
  pulados: string[]; authenticatedFetch: Fetch
  onAtualizar: (b: BoletoLimite) => void; onRemovido?: (codigo: number) => void; onClose: () => void
}) {
  // Lista congelada ao abrir: durante a geração quem ganha boleto sai de "a gerar"
  // no painel, e a lista daqui encolheria levando junto o resultado de cada linha.
  const [alvos] = useState(alvosVivos)
  const [pulados] = useState(puladosVivos)
  const padrao = new Date(); padrao.setDate(padrao.getDate() + 3)
  const [venc, setVenc] = useState(isoLocal(padrao))
  const [confirmar, setConfirmar] = useState(false)
  const [rodando, setRodando] = useState(false)
  const [feito, setFeito] = useState(false)
  const [res, setRes] = useState<Record<number, Resultado>>({})
  const parar = useRef(false)
  const total = alvos.reduce((a, l) => a + l.valor, 0)
  const hoje = isoLocal(new Date())
  const vencOk = modo === 'enviar' || (!!venc && venc >= hoje)
  const gerar = modo === 'gerar'
  // Cancelar em lote (29/10/2026): a semana 43 saiu errada e o diretor só tinha o
  // cancelar de um em um, na gaveta do boleto. Mesma rota daquele botão, uma
  // consultora por chamada, com o andamento e o "parar" do gerar/enviar.
  const cancelar = modo === 'cancelar'
  const verbo = gerar ? 'Gerar' : cancelar ? 'Cancelar' : 'Enviar'
  const nBoletos = `${alvos.length} boleto${alvos.length === 1 ? '' : 's'}`
  const jaReceberam = cancelar ? alvos.filter(a => boletos.get(a.codigo)?.status === 'enviado').length : 0

  const rodar = async () => {
    setConfirmar(false); setRodando(true); parar.current = false
    for (const a of alvos) {
      if (parar.current) break
      setRes(r => ({ ...r, [a.codigo]: { ok: false, msg: '…' } }))
      try {
        const b = boletos.get(a.codigo)
        const resp = gerar
          ? await authenticatedFetch('/api/limite-boleto/gerar', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ semana, codigo: a.codigo, nome: a.nome, valor: a.valor, vencimento: venc, estimado }),
            })
          : await authenticatedFetch(`/api/limite-boleto/${b!.id}/${cancelar ? 'cancelar' : 'enviar'}`, { method: 'POST' })
        const d = await resp.json().catch(() => ({}))
        if (d.boleto) onAtualizar(d.boleto)
        if (cancelar && d.success) onRemovido?.(a.codigo)
        setRes(r => ({ ...r, [a.codigo]: d.success ? { ok: true } : { ok: false, msg: d.error || `HTTP ${resp.status}` } }))
        // Modelo ainda não aprovado na Meta: todos os próximos falhariam igual.
        if (!d.success && /não está aprovado na Meta/.test(d.error || '')) break
      } catch (e) {
        setRes(r => ({ ...r, [a.codigo]: { ok: false, msg: e instanceof Error ? e.message : 'falha de rede' } }))
      }
    }
    setRodando(false); setFeito(true)
  }

  const oks = Object.values(res).filter(r => r.ok).length
  const falhas = Object.values(res).filter(r => !r.ok && r.msg !== '…').length
  const fechar = () => { if (!rodando) onClose() }
  const btnSec = 'h-9 rounded-lg border border-border px-4 text-sm hover:bg-secondary disabled:opacity-50'
  const btnPerigo = 'h-9 inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-4 text-sm font-medium hover:opacity-90 disabled:opacity-50'
  const btnPri = 'neu-relief h-9 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-medium hover:opacity-90 disabled:opacity-50'

  return (
    <SideModal maxWidth="max-w-xl" icon={gerar ? FiFileText : cancelar ? FiSlash : FiSend} onClose={fechar}
      title={gerar ? 'Gerar boletos do limite' : cancelar ? 'Cancelar boletos do limite' : 'Enviar boletos pelo WhatsApp'}
      subtitle={`Semana ${sem(semana)} · ${alvos.length} consultora${alvos.length === 1 ? '' : 's'} · R$ ${fmt(total)}`}
      footer={feito ? (
        <button onClick={onClose} className={btnPri}>Fechar</button>
      ) : rodando ? (
        <button onClick={() => { parar.current = true }} className={btnSec}>Parar depois desta</button>
      ) : (<>
        <button onClick={onClose} className={btnSec}>{cancelar ? 'Voltar' : 'Cancelar'}</button>
        <button onClick={() => setConfirmar(true)} disabled={!alvos.length || !vencOk} className={cancelar ? btnPerigo : btnPri}>
          {gerar ? <FiFileText className="w-4 h-4" /> : cancelar ? <FiSlash className="w-4 h-4" /> : <FiSend className="w-4 h-4" />}
          {verbo} {nBoletos}
        </button>
      </>)}>

      {/* PLANOS (21/10/2026, subtelas): corpo da gaveta em caixas com título 16px —
          "Como funciona" (explicação + vencimento), "Ficam de fora" e a lista com o
          andamento no cabeçalho. Mecânica de gerar/enviar/parar intacta. */}
      {gerar ? (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h3 className="text-base font-semibold">Como funciona</h3>
          <p className="text-sm text-muted-foreground">Um boleto Asaas para cada consultora da lista, no valor do <b className="text-foreground">Pedido − limite</b>. Nada é enviado agora: o envio é a etapa 2.</p>
          <label className="flex flex-wrap items-center gap-3 text-sm font-medium">Vencimento
            <input type="date" value={venc} min={hoje} disabled={rodando || feito} onChange={e => setVenc(e.target.value)}
              className="neu-input h-9 rounded-lg bg-card border border-border px-2.5 text-sm font-normal" />
            {!vencOk && <span className="text-[12.5px] font-normal text-red-600 dark:text-red-400">escolha uma data a partir de hoje</span>}
          </label>
          {estimado && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-300">
              <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>A semana ainda não foi faturada: os valores são <b>estimados</b> pelo preço revendedor, sem desconto, frete e corte. O boleto sai com esse valor.</span>
            </div>
          )}
        </div>
      ) : cancelar ? (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
          <h3 className="text-base font-semibold">Como funciona</h3>
          <p className="text-sm text-muted-foreground">Cada boleto da lista é <b className="text-foreground">cancelado no Asaas</b> e deixa de valer. A consultora fica livre para receber um boleto novo nesta semana.</p>
          {jaReceberam > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-300">
              <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>{jaReceberam}</b> já {jaReceberam === 1 ? 'recebeu' : 'receberam'} o boleto no WhatsApp e não {jaReceberam === 1 ? 'é avisada' : 'são avisadas'} do cancelamento.</span>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
          <h3 className="text-base font-semibold">Como funciona</h3>
          <p className="text-sm text-muted-foreground">A mensagem sai pelo número do <b className="text-foreground">Faturamento</b> para o celular do cadastro no Draco, com o valor, o vencimento, a linha digitável e o botão <b className="text-foreground">Ver boleto</b>. As respostas chegam no inbox do Faturamento.</p>
        </div>
      )}

      {pulados.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground space-y-1">
          <h3 className="text-base font-semibold text-foreground mb-1.5">Ficam de fora</h3>
          {pulados.map(p => <div key={p}>· {p}</div>)}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3 border-b border-border">
          <h3 className="text-base font-semibold">Consultoras</h3>
          {!(rodando || feito) && <span className="text-[13px] text-muted-foreground">{alvos.length} na lista</span>}
      {(rodando || feito) && (
        <div className="text-[13px] text-muted-foreground"><b className="tabular-nums text-foreground">{oks}</b> {gerar ? 'gerado' : cancelar ? 'cancelado' : 'enviado'}{oks === 1 ? '' : 's'}{falhas > 0 && <> · <b className="tabular-nums text-red-600 dark:text-red-400">{falhas}</b> com falha</>}{feito && oks + falhas < alvos.length && <> · {alvos.length - oks - falhas} não processado{alvos.length - oks - falhas === 1 ? '' : 's'}</>}</div>
      )}
        </div>
      <div className="divide-y divide-border/60">
        {alvos.map(a => {
          const r = res[a.codigo]
          return (
            <div key={a.codigo} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="w-5 shrink-0">
                {r?.msg === '…' ? <FiLoader className="w-4 h-4 animate-spin text-muted-foreground" />
                  : r?.ok ? <FiCheck className="w-4 h-4 text-emerald-600" />
                  : r ? <FiX className="w-4 h-4 text-red-600" /> : null}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate">{a.nome} <span className="text-xs text-muted-foreground tabular-nums">{a.codigo}</span></div>
                {r && !r.ok && r.msg !== '…' && <div className="text-[12.5px] text-red-600 dark:text-red-400">{r.msg}</div>}
              </div>
              <span className="tabular-nums whitespace-nowrap">R$ {fmt(a.valor)}</span>
            </div>
          )
        })}
        {!alvos.length && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Ninguém no filtro para {verbo.toLowerCase()}.</div>}
      </div>
      </div>

      <ConfirmDialog isOpen={confirmar} portal zIndex={100001} closeOnEsc type={cancelar ? 'danger' : 'warning'}
        title={`${verbo} ${nBoletos}${cancelar || gerar ? ' no Asaas' : ' pelo WhatsApp'}?`}
        message={cancelar
          ? `Total de R$ ${fmt(total)}. Boleto cancelado não volta: para cobrar de novo, é preciso gerar outro.${jaReceberam ? ` ${jaReceberam} já ${jaReceberam === 1 ? 'recebeu' : 'receberam'} a mensagem — avise que o boleto não vale mais.` : ''}`
          : gerar
          ? `São cobranças reais, total de R$ ${fmt(total)}, vencendo em ${venc ? dataBR(venc) : ''}. Para desfazer, é preciso cancelar boleto por boleto.`
          : `Cada consultora recebe a mensagem com o boleto no celular do cadastro, pelo número do Faturamento. Mensagem enviada não volta.`}
        details={[
          `Semana ${sem(semana)}`,
          ...(gerar && estimado ? ['Valores ESTIMADOS: a semana ainda não foi faturada'] : []),
          ...(pulados.length ? [`${pulados.length} grupo(s) fora da lista — veja no painel`] : []),
        ]}
        confirmText={gerar ? 'Gerar boletos' : cancelar ? 'Cancelar boletos' : 'Enviar agora'} cancelText="Voltar"
        onConfirm={rodar} onCancel={() => setConfirmar(false)} />
    </SideModal>
  )
}

// Detalhe de um boleto: link, linha digitável, envio avulso e cancelamento.
export function BoletoDetalhe({ b, authenticatedFetch, onAtualizar, onRemovido, onClose }: {
  b: BoletoLimite; authenticatedFetch: Fetch
  onAtualizar: (b: BoletoLimite) => void; onRemovido: (codigo: number) => void; onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [cancelar, setCancelar] = useState(false)
  const [enviar, setEnviar] = useState(false)
  const [copiado, setCopiado] = useState<string | null>(null)
  const descartar = b.status === 'verificar' || !b.paymentId

  const copiar = async (k: string, t: string | null) => {
    if (!t) return
    try { await navigator.clipboard.writeText(t); setCopiado(k); setTimeout(() => setCopiado(null), 1500) } catch { /* clipboard bloqueado */ }
  }
  const acao = async (url: string, depois: (d: { boleto?: BoletoLimite }) => void) => {
    setCancelar(false); setEnviar(false); setBusy(true); setErro(null)
    try {
      const r = await authenticatedFetch(url, { method: 'POST' })
      const d = await r.json().catch(() => ({}))
      if (d.boleto) onAtualizar(d.boleto)
      if (!d.success) throw new Error(d.error || `HTTP ${r.status}`)
      depois(d)
    } catch (e) { setErro(e instanceof Error ? e.message : 'falha') } finally { setBusy(false) }
  }

  const linha = (rot: string, v: ReactNode) => (
    <div className="flex gap-3 text-sm py-0.5"><span className="w-32 shrink-0 text-[13px] text-muted-foreground">{rot}</span><span className="min-w-0 flex-1 break-words">{v}</span></div>
  )
  const btn = 'h-9 inline-flex items-center gap-2 rounded-lg border border-border px-3.5 text-sm hover:bg-secondary disabled:opacity-50'

  return (
    <SideModal icon={FiFileText} title={`Boleto de ${b.nome || b.codigo}`} subtitle={`Semana ${sem(b.semana)} · código ${b.codigo}`}
      onClose={() => { if (!busy) onClose() }}
      footer={<>
        <button onClick={() => setCancelar(true)} disabled={busy || b.status === 'gerando' || b.status === 'enviando'}
          className={`${btn} text-red-600 dark:text-red-400`}><FiSlash className="w-4 h-4" />{descartar ? 'Descartar' : 'Cancelar boleto'}</button>
        {b.status === 'gerado' && <button onClick={() => setEnviar(true)} disabled={busy}
          className="neu-relief h-9 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 text-sm font-medium hover:opacity-90 disabled:opacity-50">
          {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}Enviar</button>}
      </>}>
      {/* PLANOS (21/10/2026, subtelas): os dados numa caixa com título 16px */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-2">
        <h3 className="text-base font-semibold mb-1">Dados do boleto</h3>
        {linha('Situação', <ChipBoleto b={b} onClick={() => {}} />)}
        {linha('Valor', <>R$ {fmt(b.valor)}{b.estimado && <span className="text-[12.5px] text-amber-700 dark:text-amber-300"> · estimado (semana não faturada)</span>}</>)}
        {linha('Vencimento', dataBR(b.vencimento))}
        {b.invoiceUrl && linha('Link', <span className="inline-flex flex-wrap gap-2">
          <a href={b.invoiceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><FiExternalLink className="w-3.5 h-3.5" />Abrir boleto</a>
          <button onClick={() => copiar('link', b.invoiceUrl)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><FiCopy className="w-3.5 h-3.5" />{copiado === 'link' ? 'copiado' : 'copiar'}</button>
        </span>)}
        {b.linhaDigitavel && linha('Linha digitável', <button onClick={() => copiar('linha', b.linhaDigitavel)} className="text-left tabular-nums hover:text-primary" title="copiar">{b.linhaDigitavel} <span className="text-[12.5px] text-muted-foreground">{copiado === 'linha' ? 'copiado' : ''}</span></button>)}
        {linha('Gerado', `${quando(b.geradoEm)}${b.geradoPor ? ` por ${b.geradoPor}` : ''}`)}
        {b.enviadoEm && linha('Enviado', `${quando(b.enviadoEm)} para ${b.telefone || ''}${b.enviadoPor ? ` por ${b.enviadoPor}` : ''}`)}
      </div>
      {b.status === 'verificar' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3.5 py-2.5 text-sm text-amber-800 dark:text-amber-300">
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>A geração não teve resposta e o boleto pode ter sido criado. Procure esta consultora no Asaas: se o boleto existir, cancele por lá; depois descarte aqui para liberar uma nova geração.</span>
        </div>
      )}
      {(erro || b.erro) && <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-600 dark:text-red-400">{erro || b.erro}</div>}

      <ConfirmDialog isOpen={cancelar} portal zIndex={100001} closeOnEsc type="danger"
        title={descartar ? 'Descartar este registro?' : 'Cancelar este boleto?'}
        message={descartar
          ? 'O registro sai do painel e a consultora fica livre para um novo boleto nesta semana. Faça isso só depois de conferir no Asaas que não ficou boleto pendurado.'
          : `O boleto de R$ ${fmt(b.valor)} é cancelado no Asaas e deixa de valer.${b.status === 'enviado' ? ' Ela já recebeu a mensagem: avise que este boleto não vale mais.' : ''}`}
        confirmText={descartar ? 'Descartar' : 'Cancelar boleto'} cancelText="Voltar"
        onConfirm={() => acao(`/api/limite-boleto/${b.id}/cancelar`, () => { onRemovido(b.codigo); onClose() })}
        onCancel={() => setCancelar(false)} />
      <ConfirmDialog isOpen={enviar} portal zIndex={100001} closeOnEsc type="warning"
        title="Enviar este boleto pelo WhatsApp?"
        message="A consultora recebe a mensagem com o boleto no celular do cadastro, pelo número do Faturamento."
        confirmText="Enviar agora"
        onConfirm={() => acao(`/api/limite-boleto/${b.id}/enviar`, () => {})}
        onCancel={() => setEnviar(false)} />
    </SideModal>
  )
}
